import {
	listOrganizationsQueryOptions,
	useListOrganizations,
} from "@package/api-client/hooks/organizationController";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Building2, Plus } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useCreateOrganization } from "../../../api/organizations.ts";
import { AppBaseLayout } from "../../../components/layout/app-base-layout.tsx";

export const Route = createFileRoute("/_auth/app/select-organization")({
	loader: async ({ context }) => {
		context.queryClient.ensureQueryData(listOrganizationsQueryOptions());
	},
	component: SelectOrganizationPage,
});

function SelectOrganizationPage() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { data: organizations = [] } = useListOrganizations();
	const createOrgMutation = useCreateOrganization();

	const [isCreating, setIsCreating] = useState(false);
	const [newOrgName, setNewOrgName] = useState("");
	const [newOrgSlug, setNewOrgSlug] = useState("");

	const handleCreateOrganization = async () => {
		if (!newOrgName.trim() || !newOrgSlug.trim()) return;
		try {
			const result = await createOrgMutation.mutateAsync({
				data: {
					name: newOrgName.trim(),
					slug: newOrgSlug
						.trim()
						.toLowerCase()
						.replace(/[^a-z0-9-]/g, "-"),
				},
			});
			setIsCreating(false);
			setNewOrgName("");
			setNewOrgSlug("");
			navigate({ to: "/app/org/$orgId", params: { orgId: result.id } });
		} catch (_error) {
			// Error handled by global MutationCache toast
		}
	};

	const generateSlug = (name: string) => {
		return name
			.toLowerCase()
			.replace(/[^a-z0-9\s-]/g, "")
			.replace(/\s+/g, "-")
			.replace(/-+/g, "-")
			.slice(0, 100);
	};

	return (
		<AppBaseLayout>
			<div className="min-h-screen bg-base-100 py-6 sm:py-12 px-3 sm:px-4">
				<div className="max-w-5xl mx-auto space-y-6 sm:space-y-8">
					<div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
						<div className="space-y-1 sm:space-y-2">
							<h1
								data-testid="select-org-title"
								className="text-xl sm:text-3xl font-bold text-base-content"
							>
								{t("org.selectTitle")}
							</h1>
							<p className="text-sm sm:text-base text-base-content/70">{t("org.selectSubtitle")}</p>
						</div>
						<button
							type="button"
							data-testid="select-org-create-btn"
							className="btn btn-primary w-full sm:w-auto"
							onClick={() => setIsCreating(true)}
						>
							<Plus className="h-4 w-4" />
							{t("org.createNew")}
						</button>
					</div>

					{/* Create Organization Form */}
					{isCreating && (
						<div className="card bg-base-100 shadow-lg border border-primary/20">
							<div className="card-body">
								<h3 className="card-title text-base flex items-center gap-2">
									<Building2 className="h-5 w-5 text-primary" />
									{t("org.createNew")}
								</h3>
								<div className="grid gap-4 mt-2">
									<div className="form-control">
										<label className="label" htmlFor="org-name">
											<span className="label-text">{t("org.name")}</span>
										</label>
										<input
											id="org-name"
											type="text"
											className="input input-bordered w-full"
											value={newOrgName}
											onChange={(e) => {
												setNewOrgName(e.target.value);
												setNewOrgSlug(generateSlug(e.target.value));
											}}
											maxLength={200}
										/>
									</div>
									<div className="form-control">
										<label className="label" htmlFor="org-slug">
											<span className="label-text">{t("org.slug")}</span>
										</label>
										<input
											id="org-slug"
											type="text"
											className="input input-bordered w-full"
											value={newOrgSlug}
											onChange={(e) => setNewOrgSlug(e.target.value)}
											pattern="^[a-z0-9-]+$"
											maxLength={100}
										/>
									</div>
								</div>
								<div className="card-actions justify-end mt-4">
									<button
										type="button"
										data-testid="select-org-cancel-btn"
										className="btn btn-ghost"
										onClick={() => {
											setIsCreating(false);
											setNewOrgName("");
											setNewOrgSlug("");
										}}
									>
										{t("common.cancel")}
									</button>
									<button
										type="button"
										data-testid="select-org-submit-btn"
										className="btn btn-primary"
										onClick={handleCreateOrganization}
										disabled={
											!newOrgName.trim() || !newOrgSlug.trim() || createOrgMutation.isPending
										}
									>
										{createOrgMutation.isPending ? t("common.creating") : t("common.create")}
									</button>
								</div>
							</div>
						</div>
					)}

					{/* Organization List */}
					{organizations.length === 0 && !isCreating ? (
						<div className="mt-4 sm:mt-6 rounded-lg border border-dashed border-base-300 p-6 sm:p-10 text-center">
							<Building2 className="h-12 w-12 text-base-content/30 mx-auto mb-4" />
							<h3 className="text-base sm:text-lg font-semibold text-base-content mb-2">
								{t("org.noOrganizations")}
							</h3>
							<p className="text-sm sm:text-base text-base-content/70 mb-4 sm:mb-6">
								{t("org.createFirst")}
							</p>
							<button type="button" className="btn btn-primary" onClick={() => setIsCreating(true)}>
								<Plus className="h-4 w-4" />
								{t("org.createNew")}
							</button>
						</div>
					) : (
						<div className="grid gap-4 sm:gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
							{organizations.map((org) => {
								const initials = org.name
									.split(" ")
									.map((word) => word[0])
									.join("")
									.toUpperCase()
									.slice(0, 2);

								return (
									<Link
										key={org.id}
										to="/app/org/$orgId"
										params={{ orgId: org.id }}
										data-testid="select-org-card"
										className="card bg-base-100 shadow hover:shadow-lg transition-all duration-200 border-2 border-base-300 hover:border-primary/50 cursor-pointer"
									>
										<div className="card-body">
											<div className="flex items-center gap-3">
												<div className="w-11 h-11 rounded-xl bg-linear-to-br from-primary to-primary/70 flex items-center justify-center text-primary-content font-bold text-sm shadow-sm">
													{initials}
												</div>
												<div className="flex-1 min-w-0">
													<h3 className="font-semibold text-base-content line-clamp-2">
														{org.name}
													</h3>
													<p className="text-xs text-base-content/50">{org.slug}</p>
												</div>
											</div>
										</div>
									</Link>
								);
							})}
						</div>
					)}
				</div>
			</div>
		</AppBaseLayout>
	);
}
